import { PERMISSIONS } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppStore } from '../../../lib/state/store.js';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import {
  type NotificationTemplateDetail,
  type NotificationTemplateFormInput,
  notificationTemplateFormInput,
} from '../api/schemas.js';
import { useCreateNotificationTemplate, useUpdateNotificationTemplate } from '../api/mutations.js';
import { useNotificationTemplateDetail } from '../api/queries.js';
import { NotificationTemplateFields } from '../forms/notification-template-fields.js';

/**
 * Creating and editing a notification template.
 *
 * One component for both because they are the same fields under the same rules.
 * The code is fixed once the template exists, and the channel cannot be changed
 * because it determines which table the row lives in.
 */

const CREATE_DEFAULTS: NotificationTemplateFormInput = {
  propertyId: '',
  channel: '',
  code: '',
  name: '',
  subject: '',
  body: '',
  fromAddress: '',
  fromName: '',
  isDefault: false,
  isActive: true,
};

function defaultsFrom(template: NotificationTemplateDetail): NotificationTemplateFormInput {
  return {
    propertyId: template.propertyId ?? '',
    channel: template.channel,
    code: template.code,
    name: template.name,
    subject: template.subject ?? '',
    body: template.body,
    fromAddress: template.fromAddress ?? '',
    fromName: template.fromName ?? '',
    isDefault: template.isDefault,
    isActive: template.isActive,
  };
}

export function NotificationTemplateFormPage(): React.JSX.Element {
  const templateId = useParams().templateId;
  const isEdit = templateId !== undefined;

  return (
    <PermissionGuard
      permission={isEdit ? PERMISSIONS.NOTIFICATION_CONFIGURE : PERMISSIONS.NOTIFICATION_CONFIGURE}
      fallback={<NotPermitted />}
    >
      {isEdit ? (
        <EditNotificationTemplate templateId={templateId} />
      ) : (
        <CreateNotificationTemplate />
      )}
    </PermissionGuard>
  );
}

function CreateNotificationTemplate(): React.JSX.Element {
  const navigate = useNavigate();
  const create = useCreateNotificationTemplate();
  const propertyId = useAppStore(
    (state) => state.ui.lastPropertyIds?.['notification-templates'] ?? '',
  );

  const defaultValues = useMemo(() => ({ ...CREATE_DEFAULTS, propertyId }), [propertyId]);

  const form = useAppForm<NotificationTemplateFormInput>({
    schema: notificationTemplateFormInput,
    defaultValues,
    successMessage: 'Notification template created',
    onSubmit: async (values) => {
      const created = await create.mutateAsync(values);
      void navigate(`/notification-templates/${created.id}`);
    },
  });

  return (
    <FormPage
      title="New notification template"
      description="The message a guest receives for one channel and one notification type."
      backTo="/notification-templates"
      backLabel="Notification templates"
      submitLabel="Create template"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <NotificationTemplateFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        mode="create"
      />
    </FormPage>
  );
}

function EditNotificationTemplate({
  templateId,
}: {
  readonly templateId: string;
}): React.JSX.Element {
  const navigate = useNavigate();
  const update = useUpdateNotificationTemplate();
  const query = useNotificationTemplateDetail(templateId);

  if (query.isPending) return <LoadingState label="Loading notification template" />;
  if (query.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(query.error) ?? ''}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <EditNotificationTemplateForm
      template={query.data}
      onSaved={() => {
        void navigate(`/notification-templates/${templateId}`);
      }}
      save={async (values) => {
        await update.mutateAsync({ templateId, values });
      }}
    />
  );
}

function EditNotificationTemplateForm({
  template,
  onSaved,
  save,
}: {
  readonly template: NotificationTemplateDetail;
  readonly onSaved: () => void;
  readonly save: (values: NotificationTemplateFormInput) => Promise<void>;
}): React.JSX.Element {
  const form = useAppForm<NotificationTemplateFormInput>({
    schema: notificationTemplateFormInput,
    defaultValues: defaultsFrom(template),
    successMessage: 'Notification template updated',
    onSubmit: async (values) => {
      await save(values);
      onSaved();
    },
  });

  return (
    <FormPage
      title={`Edit ${template.name}`}
      description="Changes take effect immediately for the next notification of this type."
      backTo="/notification-templates"
      backLabel="Notification templates"
      submitLabel="Save changes"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <NotificationTemplateFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
        mode="edit"
      />
    </FormPage>
  );
}
